import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { MonthlyCard, WeekdayHeader, DailyDetailCard } from './components';
import ContentArea from '../../components/contentArea';
import { physicalRecordAPI } from '../../actions/imsServer';
import { formatDateTime } from '../../utils/base';
import { EXERCISE_TARGETS, API_PAGE_SIZE } from '../../constants';

export const ReportActivityMonthly = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const scrollContainerRef = useRef(null);
    const currentMonthRef = useRef(null);
    const hasInitialScrolledToCurrentMonth = useRef(false);

    // API 데이터 상태
    const [physicalRecords, setPhysicalRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 사용자 정보 가져오기
    const meInfo = JSON.parse(localStorage.getItem('meInfo') || '{}');

    // 월간 신체 기록 데이터 가져오기 (전체 데이터)
    const fetchMonthlyPhysicalRecords = async () => {
        if (!meInfo.id) return;

        try {
            setLoading(true);
            setError(null);

            // 전체 기간 데이터를 100건씩 배치 조회
            const fetchPhysicalRecordsBatched = async () => {
                let all = [];
                let offset = 0;
                let page;
                do {
                    const res = await physicalRecordAPI.getPhysicalRecords({
                        userId: meInfo.id,
                        state: 0,
                        limit: API_PAGE_SIZE,
                        offset
                    });
                    page = res?.data?.physicalRecords || [];
                    all = all.concat(page);
                    offset += API_PAGE_SIZE;
                } while (page.length === API_PAGE_SIZE);
                return all;
            };

            const allRecords = await fetchPhysicalRecordsBatched();

            setPhysicalRecords(allRecords);
        } catch (err) {
            console.error('월간 신체 기록 데이터 로딩 실패:', err);
            setError(err.message);
            setPhysicalRecords([]);
        } finally {
            setLoading(false);
        }
    };

    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
        fetchMonthlyPhysicalRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchMonthlyPhysicalRecords는 의존성에서 제외
    }, [meInfo.id]);

    // 초기 진입 시 이번 달 카드가 보이도록 스크롤
    useEffect(() => {
        if (loading || error || hasInitialScrolledToCurrentMonth.current) return;
        if (scrollContainerRef.current && currentMonthRef.current) {
            const container = scrollContainerRef.current;
            const target = currentMonthRef.current;
            const containerTop = container.getBoundingClientRect().top;
            const targetTop = target.getBoundingClientRect().top;
            const currentScrollTop = container.scrollTop;
            const scrollOffset = targetTop - containerTop + currentScrollTop;

            container.scrollTo({
                top: Math.max(0, scrollOffset - 20),
                behavior: 'auto'
            });
            hasInitialScrolledToCurrentMonth.current = true;
        }
    }, [loading, error]);

    // 날짜 선택 시 해당 월 달력으로 스크롤
    const selectedMonthRef = useRef(null);

    useEffect(() => {
        if (selectedDate && selectedMonth && selectedYear && selectedMonthRef.current && scrollContainerRef.current) {
            setTimeout(() => {
                const container = scrollContainerRef.current;
                const targetElement = selectedMonthRef.current;

                if (container && targetElement) {
                    // 컨테이너의 상단 위치
                    const containerTop = container.getBoundingClientRect().top;
                    // 타겟 요소의 상단 위치
                    const targetTop = targetElement.getBoundingClientRect().top;
                    // 현재 스크롤 위치
                    const currentScrollTop = container.scrollTop;
                    // 타겟 요소까지의 스크롤 거리 계산
                    const scrollOffset = targetTop - containerTop + currentScrollTop;

                    // 적절한 여백을 위해 약간 위로 스크롤 (예: 20px)
                    container.scrollTo({
                        top: Math.max(0, scrollOffset - 20),
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }, [selectedDate, selectedMonth, selectedYear]);

    // 선택한 날짜의 실제 데이터 가져오기
    const getDailyData = (date, month, year) => {
        const targetDate = new Date(year, month - 1, date);
        const targetDateStr = formatDateTime(targetDate, '.', ':', 0, 10);

        const dayRecord = physicalRecords.find(record => {
            const recordDateStr = formatDateTime(record.date, '.', ':', 0, 10);
            return recordDateStr === targetDateStr;
        });

        if (dayRecord) {
            return {
                totalSteps: dayRecord.totalSteps || 0,
                calories: dayRecord.calories || 0,
                totalTime: dayRecord.totalTime || 0,
            };
        }

        return {
            totalSteps: 0,
            calories: 0,
            totalTime: 0,
        };
    };

    // Monthly exercise data - 실제 데이터 기반 (중간 빈 달 포함)
    const monthlyData = useMemo(() => {
        // 월간 통계 계산 함수
        const calculateMonthlyStats = (records, month, year) => {
            const totalSteps = records.reduce((sum, record) => sum + (record.totalSteps || 0), 0);
            const totalCalories = records.reduce((sum, record) => sum + (record.calories || 0), 0);
            const totalTime = records.reduce((sum, record) => sum + (record.totalTime || 0), 0);

            // 목표달성 기준: 3가지 조건을 모두 만족해야 함
            const achievedDays = records.filter(record => {
                const steps = record.totalSteps || 0;
                const calories = record.calories || 0;
                const time = record.totalTime || 0;

                return steps >= EXERCISE_TARGETS.STEPS && calories >= EXERCISE_TARGETS.CALORIES && time >= EXERCISE_TARGETS.ACTIVITY_TIME;
            }).length;

            // 목표달성일 계산: min(이번달마지막일, 오늘)
            const currentDate = new Date();
            const isCurrentMonth = month === currentDate.getMonth() + 1 && year === currentDate.getFullYear();
            const lastDayOfMonth = new Date(year, month, 0).getDate();
            const today = currentDate.getDate();

            const goalDays = isCurrentMonth ? Math.min(lastDayOfMonth, today) : lastDayOfMonth;

            return {
                stepCount: totalSteps,
                calories: totalCalories,
                time: totalTime,
                achievedDays,
                goalDays
            };
        };

        // 월별 그룹핑
        const recordsByMonth = physicalRecords.reduce((acc, record) => {
            const recordDate = new Date(record.date);
            if (Number.isNaN(recordDate.getTime())) return acc;

            const year = recordDate.getFullYear();
            const month = recordDate.getMonth() + 1;
            const key = `${year}-${String(month).padStart(2, '0')}`;

            if (!acc[key]) {
                acc[key] = {
                    year,
                    month,
                    records: [],
                };
            }
            acc[key].records.push(record);
            return acc;
        }, {});

        const now = new Date();
        const sortedMonths = Object.values(recordsByMonth)
            .sort((a, b) => (a.year - b.year) || (a.month - b.month));

        // 데이터가 없는 경우에는 이번 달만 표시
        if (sortedMonths.length === 0) {
            const currentYear = now.getFullYear();
            const currentMonth = now.getMonth() + 1;
            const stats = calculateMonthlyStats([], currentMonth, currentYear);
            return [{
                month: currentMonth,
                year: currentYear,
                goalDays: stats.goalDays,
                achievedDays: stats.achievedDays,
                stepCount: stats.stepCount,
                calories: stats.calories,
                time: stats.time,
                dailyData: [],
                isCurrentMonth: true
            }];
        }

        const first = sortedMonths[0];
        const range = [];
        let cursorYear = first.year;
        let cursorMonth = first.month;
        const nowYear = now.getFullYear();
        const nowMonth = now.getMonth() + 1;

        // 첫 데이터 월 ~ 이번 달까지 연속 월 생성(중간 빈 달 포함)
        while (cursorYear < nowYear || (cursorYear === nowYear && cursorMonth <= nowMonth)) {
            range.push({ year: cursorYear, month: cursorMonth });
            cursorMonth += 1;
            if (cursorMonth > 12) {
                cursorMonth = 1;
                cursorYear += 1;
            }
        }

        return range.map(({ year, month }) => {
            const key = `${year}-${String(month).padStart(2, '0')}`;
            const records = recordsByMonth[key]?.records || [];
                const stats = calculateMonthlyStats(records, month, year);
                const isCurrentMonth = month === now.getMonth() + 1 && year === now.getFullYear();

                return {
                    month,
                    year,
                    goalDays: stats.goalDays,
                    achievedDays: stats.achievedDays,
                    stepCount: stats.stepCount,
                    calories: stats.calories,
                    time: stats.time,
                    dailyData: records,
                    isCurrentMonth
                };
            });
    }, [physicalRecords]);

    return (
        <ContentArea sx={{
            height: '100vh',
            overflow: 'hidden',
            paddingBottom: 0,
            width: '100%', px: '10px'
        }}>
            {/* Loading State */}
            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1">{t('loading_data')}</Typography>
                </Box>
            )}

            {/* Error State */}
            {error && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="error">
                        {t('an_error_occurred_while_loading_data_message', { message: error })}
                    </Typography>
                </Box>
            )}

            {/* Content */}
            {!loading && !error && (
                <>
                    {/* Weekday Header - Fixed */}
                    <WeekdayHeader />

                    {/* Scrollable Content */}
                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            width: '100%',
                            flex: 1,
                            minHeight: 0,
                            overflowY: 'auto',
                            paddingBottom: '100px',
                            backgroundColor: theme.palette.figma.mainBg,
                        }}
                    >
                        {monthlyData.map((month) => (
                            <React.Fragment key={`${month.year}-${month.month}`}>
                                <Box
                                    ref={(el) => {
                                        if (month.isCurrentMonth) {
                                            currentMonthRef.current = el;
                                        }
                                        if (selectedDate && selectedMonth === month.month && selectedYear === month.year) {
                                            selectedMonthRef.current = el;
                                        }
                                    }}
                                >
                                    <MonthlyCard
                                        month={month.month}
                                        year={month.year}
                                        goalDays={month.goalDays}
                                        achievedDays={month.achievedDays}
                                        stepCount={month.stepCount}
                                        calories={month.calories}
                                        time={month.time}
                                        isCurrentMonth={month.isCurrentMonth}
                                        selectedDate={selectedDate && selectedMonth === month.month && selectedYear === month.year ? selectedDate : null}
                                        monthlyData={month} // 해당 월의 데이터만 전달
                                        onDateClick={(day, month, year) => {
                                            setSelectedDate(day);
                                            setSelectedMonth(month);
                                            setSelectedYear(year);
                                        }}
                                    />
                                </Box>

                                {/* Daily Detail Card - 선택된 날짜가 해당 월에 속하면 해당 월 달력 바로 아래에 표시 */}
                                {selectedDate && selectedMonth === month.month && selectedYear === month.year && (() => {
                                    const dailyData = getDailyData(selectedDate, selectedMonth, selectedYear);

                                    // 현재 날짜 정보
                                    const currentDate = new Date();

                                    // 날짜 경계 조건 확인
                                    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
                                    const selectedDateObj = new Date(selectedYear, selectedMonth - 1, selectedDate);

                                    // 이동 가능한 날짜 범위: 데이터가 있는 첫 월 1일 ~ 오늘
                                    const firstMonth = monthlyData[0];
                                    const startDate = firstMonth
                                        ? new Date(firstMonth.year, firstMonth.month - 1, 1)
                                        : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                                    const endDate = new Date(
                                        currentDate.getFullYear(),
                                        currentDate.getMonth(),
                                        currentDate.getDate()
                                    );

                                    // 이전달 정보(연/월 경계 처리)
                                    const prevMonth = selectedMonth === 1 ? 12 : selectedMonth - 1;
                                    const prevYear = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
                                    const prevMonthDays = new Date(prevYear, prevMonth, 0).getDate(); // 이전 달의 일수
                                    const nextMonth = selectedMonth === 12 ? 1 : selectedMonth + 1;
                                    const nextYear = selectedMonth === 12 ? selectedYear + 1 : selectedYear;

                                    // 이동 가능 여부 확인
                                    const canGoPrevious = selectedDateObj > startDate; // 시작 날짜보다 이후인 경우
                                    const canGoNext = selectedDateObj < endDate; // 현재 날짜보다 이전인 경우

                                    return (
                                        <DailyDetailCard
                                            selectedDate={selectedDate}
                                            selectedMonth={selectedMonth}
                                            selectedYear={selectedYear}
                                            totalSteps={dailyData.totalSteps}
                                            calories={dailyData.calories}
                                            totalTime={dailyData.totalTime}
                                            showPrevious={canGoPrevious}
                                            showNext={canGoNext}
                                            onClose={() => {
                                                setSelectedDate(null);
                                                setSelectedMonth(null);
                                                setSelectedYear(null);
                                            }}
                                            onPrevious={() => {
                                                if (selectedDate > 1) {
                                                    // 같은 달 내에서 이전 날짜로
                                                    setSelectedDate(selectedDate - 1);
                                                } else {
                                                    // 이전달 말일로 이동
                                                    setSelectedDate(prevMonthDays);
                                                    setSelectedMonth(prevMonth);
                                                    setSelectedYear(prevYear);
                                                }
                                            }}
                                            onNext={() => {
                                                if (selectedDate < daysInMonth) {
                                                    // 같은 달 내에서 다음 날짜로
                                                    setSelectedDate(selectedDate + 1);
                                                } else {
                                                    // 다음달 1일로 이동
                                                    setSelectedDate(1);
                                                    setSelectedMonth(nextMonth);
                                                    setSelectedYear(nextYear);
                                                }
                                            }}
                                        />
                                    );
                                })()}
                            </React.Fragment>
                        ))}
                    </Box>
                </>
            )}
        </ContentArea>
    );
};
